import './configuracao-garantia-modal.js';
import type { Garantia, GarantiaConfiguracao } from '@pgcustom/shared-types';
import type { Resultado } from '../../core/resultado.js';
import type { PgTable, PgTableColumn, PgTableRow, PgKpiCard } from '@pgcustom/design-system';
import type { GarantiasApi } from './garantias.api.js';
import type { PermissoesGarantias } from './garantias.permissoes.js';
import { formatarDataHora } from '../pedidos/formatacao.js';
import type { ErpConfiguracaoGarantiaModal } from './configuracao-garantia-modal.js';

const COLUNAS: PgTableColumn[] = [
  { key: 'pedidoId', label: 'Pedido' },
  { key: 'clienteId', label: 'Cliente' },
  { key: 'criadoEm', label: 'Aberta em' },
  { key: 'expiraEm', label: 'Expira em' },
  { key: 'status', label: 'Status' },
];

/**
 * `<erp-garantias-page>` — Garantias (Sprint 10, `ROADMAP.md`/
 * `PRODUCT.md` 3.7). Sem criação manual — toda garantia nasce do
 * listener de `envio.entregue`, contanto que `diasCobertura` já tenha
 * sido configurado (botão "Configurar cobertura", admin). Clique numa
 * garantia ainda não expirada solicita reparo (`POST
 * /garantias/:id/reparo`) — a OPS de reparo, se criada, aparece em
 * Produção (mesmo padrão de "ação real direta no clique de linha" de
 * `<erp-envios-page>`).
 */
export class ErpGarantiasPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _garantiasApi: GarantiasApi | null = null;
  private _permissoes: PermissoesGarantias = { ler: false, atualizar: false };

  private resultadoGarantias: Resultado<Garantia[]> = { status: 'carregando' };
  private garantiasAtuais: Garantia[] = [];
  private configuracao: GarantiaConfiguracao | null = null;
  private mensagemAcao = '';

  private modalConfiguracao!: ErpConfiguracaoGarantiaModal;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set garantiasApi(valor: GarantiasApi) {
    this._garantiasApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set permissoes(valor: PermissoesGarantias) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._garantiasApi) void this.carregar();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .topo { display: flex; align-items: center; justify-content: space-between; gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-5, 24px); flex-wrap: wrap; }
        h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-xl, 1.5rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: 0; }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-top: 2px; }
        .config { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-desabilitado, #5c626b); margin-bottom: var(--pg-space-4, 16px); }
        .dica { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-desabilitado, #5c626b); margin-top: var(--pg-space-3, 12px); }
        .erro-acao { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-erro, #e5484d); margin-top: var(--pg-space-3, 12px); }
        .erro-acao:empty { display: none; }
        .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-4, 16px); }
      </style>
      <div class="topo">
        <div>
          <h1>Garantias</h1>
          <div class="subtitulo">Garantias abertas automaticamente na entrega — clique para solicitar reparo</div>
        </div>
        ${this._permissoes.atualizar ? '<pg-button id="botao-configurar" variant="secondary">Configurar cobertura</pg-button>' : ''}
      </div>
      <p class="config">${this.textoConfiguracao()}</p>

      <div class="kpis">
        <pg-kpi-card id="kpi-ativas" label="Ativas" icone="garantias" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-expiradas" label="Expiradas" icone="garantias" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-total" label="Total" icone="garantias" estado="carregando"></pg-kpi-card>
      </div>

      <pg-card padding="compacto">
        <div id="garantias"></div>
      </pg-card>
      ${this._permissoes.atualizar ? '<p class="dica">Clique em uma garantia ainda não expirada para solicitar reparo.</p>' : ''}
      <p class="erro-acao">${escaparHtml(this.mensagemAcao)}</p>

      <erp-configuracao-garantia-modal id="modal-configuracao"></erp-configuracao-garantia-modal>
    `;

    this.modalConfiguracao = this.root.querySelector('#modal-configuracao') as ErpConfiguracaoGarantiaModal;
    if (this._garantiasApi) this.modalConfiguracao.garantiasApi = this._garantiasApi;
    this.modalConfiguracao.aoSalvar = (configuracao) => {
      this.configuracao = configuracao;
      this.renderEsqueleto();
    };

    this.root.querySelector('#botao-configurar')?.addEventListener('pg-click', () => {
      this.modalConfiguracao.abrir(this.configuracao?.diasCobertura ?? null);
    });

    this.root.querySelector('#garantias')?.addEventListener('pg-row-click', (evento) => {
      if (!this._permissoes.atualizar) return;
      const { index } = (evento as CustomEvent<{ row: PgTableRow; index: number }>).detail;
      const garantia = this.garantiasAtuais[index];
      if (garantia && !garantia.expirada) void this.solicitarReparo(garantia.id);
    });

    this.atualizar();
  }

  private textoConfiguracao(): string {
    if (!this.configuracao) return '';
    return this.configuracao.diasCobertura !== null
      ? `Cobertura atual: ${this.configuracao.diasCobertura} dias a partir da entrega.`
      : 'Nenhuma cobertura configurada ainda — garantias não são abertas automaticamente até isso ser definido.';
  }

  private async solicitarReparo(id: string): Promise<void> {
    if (!this._garantiasApi) return;
    try {
      const { opsId } = await this._garantiasApi.solicitarReparo(id);
      this.mensagemAcao = opsId
        ? ''
        : 'reparo solicitado, mas nenhuma OPS pôde ser criada — verifique o pedido em Produção.';
      void this.carregar();
    } catch {
      this.mensagemAcao = 'não foi possível solicitar reparo. tente novamente.';
      this.renderEsqueleto();
    }
  }

  private async carregar(): Promise<void> {
    const api = this._garantiasApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultadoGarantias = { status: 'sem-permissao' };
      this.atualizar();
      return;
    }

    this.resultadoGarantias = { status: 'carregando' };
    this.atualizar();

    if (this._permissoes.atualizar) {
      this.configuracao = await api.obterConfiguracao();
    }
    this.resultadoGarantias = await api.listar();
    this.renderEsqueleto();
  }

  private atualizar(): void {
    const container = this.root.querySelector('#garantias');
    if (!container) return;

    if (this.resultadoGarantias.status === 'ok') {
      this.garantiasAtuais = this.resultadoGarantias.dado;
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS;
      tabela.rows = this.garantiasAtuais.map((g) => ({
        pedidoId: g.pedidoId.slice(0, 8) + '…',
        clienteId: g.clienteId.slice(0, 8) + '…',
        criadoEm: formatarDataHora(g.criadoEm),
        expiraEm: formatarDataHora(g.expiraEm),
        status: g.expirada ? { texto: 'Expirada', tom: 'neutro' as const } : { texto: 'Ativa', tom: 'sucesso' as const },
      }));
      this.atualizarKpis(this.garantiasAtuais);
      return;
    }

    this.garantiasAtuais = [];
    const { variant, titulo, descricao } = estadoVazio(this.resultadoGarantias);
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    for (const id of ['kpi-ativas', 'kpi-expiradas', 'kpi-total']) {
      const kpi = this.root.querySelector(`#${id}`) as PgKpiCard | null;
      if (!kpi) continue;
      if (this.resultadoGarantias.status === 'vazio') {
        kpi.setAttribute('estado', 'ok');
        kpi.setAttribute('valor', '0');
      } else {
        kpi.setAttribute('estado', variant === 'carregando' ? 'carregando' : 'indisponivel');
      }
    }
  }

  private atualizarKpis(garantias: Garantia[]): void {
    const expiradas = garantias.filter((g) => g.expirada).length;
    const ativas = garantias.length - expiradas;
    (this.root.querySelector('#kpi-ativas') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-ativas') as PgKpiCard | null)?.setAttribute('valor', String(ativas));
    (this.root.querySelector('#kpi-expiradas') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-expiradas') as PgKpiCard | null)?.setAttribute('valor', String(expiradas));
    (this.root.querySelector('#kpi-total') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-total') as PgKpiCard | null)?.setAttribute('valor', String(garantias.length));
  }
}

function estadoVazio(resultado: Resultado<Garantia[]>): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo: 'Garantias', descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo: 'Garantias', descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo: 'Garantias', descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhuma garantia', descricao: 'garantias aparecem aqui automaticamente quando um envio é entregue' };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-garantias-page', ErpGarantiasPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-garantias-page': ErpGarantiasPage;
  }
}
